import { useEffect, useRef, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { faInt } from '../format.js';
import type { AnswerFooter } from '../types.js';

export interface ResponseFooterProps {
  readonly footer: AnswerFooter;
  /** Plain text copied by «کپی». */
  readonly copyText: string;
}

type Feedback = 'up' | 'down' | null;

const COPIED_MS = 1600;

/**
 * Component 8 — response footer: basis line, token usage (only when there is some),
 * copy, correct / incorrect, and the «نمونه» tag on scripted samples.
 *
 * Honesty notes: «کپی» really copies. «خروجی» is shown disabled — export lives
 * in the central /data route, no per-answer export endpoint exists. The two
 * feedback buttons only toggle local state; nothing is sent anywhere.
 */
export function ResponseFooter({ footer, copyText }: ResponseFooterProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {
      return;
    }
    setCopied(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
  }

  // A notice (no basis, no usage — e.g. «needs more scope») has nothing to copy, export or rate.
  const isNotice = !footer.basis && footer.tokens === 0;

  return (
    <div className="ai-block ai-footer">
      {footer.basis ? <span className="ai-footer__basis">{footer.basis}</span> : null}
      <span className="ai-footer__actions">
        {footer.usageLabel || footer.tokens > 0 ? (
          <span className="ai-footer__usage">
            {footer.usageLabel ? (
              footer.usageLabel
            ) : (
              <>
                مصرف <span className="sk-num">{faInt(footer.tokens)}</span> {footer.unit ?? 'توکن'}
              </>
            )}
          </span>
        ) : null}
        {footer.sample === false ? null : (
          <span className="sk-tag ai-sample-tag" title="این پاسخ نمونه است، نه دادهٔ واقعی">
            نمونه
          </span>
        )}
        {isNotice ? null : (
          <>
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--sm"
              onClick={() => void copy()}
            >
              {copied ? 'کپی شد' : 'کپی'}
            </button>
            <span className="ai-footer__sep" aria-hidden="true" />
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
              title="درست بود"
              aria-label="درست بود"
              aria-pressed={feedback === 'up'}
              data-active={feedback === 'up'}
              onClick={() => setFeedback((value) => (value === 'up' ? null : 'up'))}
            >
              <Icon name="check" size="sm" />
            </button>
            <button
              type="button"
              className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
              title="درست نبود"
              aria-label="درست نبود"
              aria-pressed={feedback === 'down'}
              data-active={feedback === 'down'}
              onClick={() => setFeedback((value) => (value === 'down' ? null : 'down'))}
            >
              <Icon name="x" size="sm" />
            </button>
          </>
        )}
      </span>
    </div>
  );
}
